import { Delete } from '@mui/icons-material'
import { Controller, useFormContext } from 'react-hook-form'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { ClassNameValue } from 'tailwind-merge'
import {
	useOrganizationGroups,
	useOrganizationRoles,
	useOrganizationUsers,
} from '../../hooks/api/organizationHooks'
import cn from '../../lib/utils/cn'
import { IconButton, Input, Select } from '../ui'
import { useAuth } from '../../hooks'

export type PermissionSubject = 'users' | 'groups' | 'roles'

interface PermissionLevelOption {
	value: number
	label: string
}

const DEFAULT_LEVEL_OPTIONS: PermissionLevelOption[] = [
	{ value: 0, label: 'Read' },
	{ value: 1, label: 'Write' },
	{ value: 2, label: 'Delete' },
	{ value: 3, label: 'None' },
]

// currentBlank.type encoding: User = 0, Group = 1, Role = 2
const SUBJECT_CONFIG: Record<
	PermissionSubject,
	{ type: number; label: string }
> = {
	users: { type: 0, label: 'User' },
	groups: { type: 1, label: 'Group' },
	roles: { type: 2, label: 'Role' },
}

interface PermissionBlankProps {
	className?: ClassNameValue
	subjects?: PermissionSubject[]
	levelOptions?: PermissionLevelOption[]
	currentPermissions?: { id: string }[]
}

const PermissionBlank: FC<PermissionBlankProps> = ({
	className,
	subjects = ['users', 'groups', 'roles'],
	levelOptions = DEFAULT_LEVEL_OPTIONS,
	currentPermissions,
}) => {
	const { currentUser, isAuthenticated } = useAuth()
	const [search, setSearch] = useState('')

	const { setValue, watch, control } = useFormContext<any>()
	const currentBlank = watch('currentBlank')
	const permissions = (watch('permissions') ?? []) as { id: string }[]

	const orgId = currentUser?.organization?.id ?? ''

	useEffect(() => {
		!currentBlank?.name && setSearch('')
	}, [currentBlank?.name])

	const { data: usersData } = useOrganizationUsers(
		{ id: orgId },
		{
			enabled:
				subjects.includes('users') && Boolean(orgId && isAuthenticated),
		},
	)
	const { data: groupsData } = useOrganizationGroups(
		{ id: orgId },
		{
			enabled:
				subjects.includes('groups') && Boolean(orgId && isAuthenticated),
		},
	)
	const { data: rolesData } = useOrganizationRoles(
		{ id: orgId },
		{
			enabled:
				subjects.includes('roles') && Boolean(orgId && isAuthenticated),
		},
	)

	const typeOptions = useMemo(
		() =>
			subjects.map((s) => ({
				value: SUBJECT_CONFIG[s].type,
				label: SUBJECT_CONFIG[s].label,
			})),
		[subjects]
	)

	const inputOptions = useMemo(() => {
		if (!currentUser?.id) return []

		const type = Number(currentBlank?.type)
		const entries =
			type === 1
				? groupsData?.data?.entries
				: type === 2
					? rolesData?.data?.entries
					: usersData?.data?.entries

		return (
			entries
				?.filter((item) => {
					const email = 'email' in item ? item.email : ''
					const isAdminUser =
						'email' in item
							? item?.roles?.some((r) => r?.isAdministrator) ||
							item?.isDeactivated
							: false
					const isPermanentRole =
						'isPermanent' in item ? item?.isPermanent : false

					if (
						item?.id?.toString() === currentUser?.id?.toString() ||
						isAdminUser
					) {
						return false
					}

					const isExcluded = [
						...(currentPermissions ?? []),
						...permissions,
					]?.some(
						(p) => item?.id?.toString() === p?.id?.toString() || isAdminUser
					)

					return (
						!isExcluded &&
						(item?.name + email)
							?.toLowerCase()
							?.includes(search?.toLowerCase()) &&
						!isPermanentRole
					)
				})
				?.map((item) => ({
					label: (item?.name || ('email' in item ? item.email : '')) ?? '',
					id: item?.id,
				})) ?? []
		)
	}, [
		search,
		currentPermissions,
		permissions,
		currentBlank?.type,
		groupsData,
		usersData,
		rolesData,
		currentUser?.id,
	])

	return (
		<div
			className={cn(
				'h-12 py-1.5 w-full flex gap-1 md:gap-2 items-center px-1 md:px-2 justify-between border-t border-stroke-light dark:border-stroke-light-dark  box-border bg-background-light dark:bg-background-light-dark',
				className
			)}
		>
			<Controller
				name="currentBlank.type"
				control={control}
				render={({ field: { value, onChange } }) => (
					<Select
						wrapperClassName="w-1/4 md:w-36"
						className="h-9 w-full text-sm box-border"
						value={value ?? 0}
						onChange={(e) => {
							if (currentBlank?.type !== e) {
								setValue('currentBlank.name', '')
								setValue('currentBlank.id', null)
								onChange(e)
							}
						}}
						options={typeOptions}
						disabled={typeOptions.length <= 1}
					/>
				)}
			/>

			<Input
				wrapperClassName="h-9 box-border w-3/5 sm:w-[180px] text-sm relative"
				inputClassName="px-2 w-full"
				itemsWrapperClassName="max-h-[180px]"
				onChange={(e) => {
					setValue('currentBlank.id', null)
					setValue('currentBlank.name', '')
					setSearch(e?.currentTarget?.value)
				}}
				value={currentBlank?.name || search}
				withItems
				items={inputOptions}
				onItemClick={(item) => {
					setValue('currentBlank.id', item?.id)
					setValue('currentBlank.name', item?.label ?? '')
				}}
			/>

			<Controller
				name={'currentBlank.level'}
				control={control}
				render={({ field: { value, onChange } }) => (
					<Select
						wrapperClassName="w-1/4 md:w-36"
						className="h-9 box-border w-full"
						value={value ?? 0}
						onChange={onChange}
						options={levelOptions}
					/>
				)}
			/>

			<IconButton
				icon={<Delete className="w-5 dark:text-text-main-dark" />}
				variant="secondary"
				onClick={() => setValue('currentBlank', null)}
				tooltip={'Delete permission blank'}
				className="h-9 w-9 min-w-9"
			/>
		</div>
	)
}

export default PermissionBlank
